import { lazy, Suspense } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { useUser } from "@mfox/shared/contexts/UserContext";
import { demoShortcuts, playgroundEnabled } from "@mfox/shared/lib/demoMode";
import { ProtectedRoute } from "@mfox/shared/ui/ProtectedRoute";
import { gated } from "@mfox/shared/ui/ComingSoonPage";
import { LoginPage } from "@/features/auth/components/LoginPage";
import { KeycloakLoginPage } from "@/features/auth/components/KeycloakLoginPage";
import { keycloakAuth } from "@mfox/shared/auth/keycloak";
import { ActivatePage } from "@/features/auth/components/ActivatePage";
import { LandingPage } from "@/features/landing/components/LandingPage";
import { LoadingScreen } from "@mfox/shared/ui/StatusScreen";
import { AppShell } from "./AppShell";
import { NotFoundPage } from "./NotFoundPage";
import { usePageTitle } from "./pageTitle";

// Served by the admin remote.
const AdminRoutes = lazy(() => import("@mfox/admin/AdminRoutes"));
const CollegeAdminRoutes = lazy(() => import("@mfox/admin/CollegeAdminRoutes"));

// Served by the student home remote.
const StudentHomePage = lazy(() => import("@mfox/student-home/StudentHomePage"));
const LeaderboardPage = lazy(() => import("@mfox/student-home/LeaderboardPage"));
const RoadmapPage = lazy(() => import("@mfox/student-home/RoadmapPage"));
const ProfilePage = lazy(() => import("@mfox/student-home/ProfilePage"));
const SkillsPage = lazy(() => import("@mfox/student-home/SkillsPage"));
const AspirationPage = lazy(() => import("@mfox/student-home/AspirationPage"));
const ResultsPage = lazy(() => import("@mfox/student-home/ResultsPage"));

// Served by the mentor assessment remote.
const MentorAssessmentRoutes = lazy(() => import("@mfox/mentor-assessment/InShellRoutes"));
const MentorAssessmentWorkspacePage = lazy(() => import("@mfox/mentor-assessment/MentorAssessmentWorkspacePage"));
const MentorEvaluationPage = lazy(() => import("@mfox/mentor-assessment/MentorEvaluationPage"));
const MentorRoutes = lazy(() => import("@mfox/mentor-assessment/MentorRoutes"));

// Served by the learning remote.
const TestListPage = lazy(() => import("@mfox/learning/TestListPage"));
const TestPageRoute = lazy(() => import("@mfox/learning/TestRoute"));
const CourseHomeRoute = lazy(() => import("@mfox/learning/CourseHomeRoute"));
const CoursePageRoute = lazy(() => import("@mfox/learning/CoursePage"));
const TopicListPage = lazy(() => import("@mfox/learning/TopicListPage"));
const ProblemListRoute = lazy(() => import("@mfox/learning/ProblemListRoute"));
const PlaygroundRoute = lazy(() => import("@mfox/learning/PlaygroundRoute"));
// Served by the opportunities remote.
const OpportunitiesStudentRoutes = lazy(() => import("@mfox/opportunities/StudentRoutes"));

function HomeRedirect() {
  const { currentUser } = useUser();

  if (!currentUser) return <LandingPage />;

  if (currentUser.role === "student" && currentUser.onboardingComplete === false) {
    return <Navigate to="/onboarding/skills" replace />;
  }

  if (currentUser.role === "mfox") {
    return <Navigate to="/admin" replace />;
  }
  if (currentUser.role === "college-admin") {
    return <Navigate to="/admin/college" replace />;
  }
  if (currentUser.role === "mentor") {
    return <Navigate to="/mentor" replace />;
  }
  return <Navigate to="/home" replace />;
}

export function AppRoutes() {
  usePageTitle();
  return (
    <Suspense fallback={<LoadingScreen message="Loading…" />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={keycloakAuth ? <KeycloakLoginPage /> : <LoginPage />} />
        <Route path="/activate" element={keycloakAuth ? <Navigate to="/login" replace /> : <ActivatePage />} />

        {/* Home redirect based on role */}
        <Route path="/" element={<HomeRedirect />} />

        {/* Onboarding wizard — students with onboardingComplete: false are
            redirected here by every other ProtectedRoute below. Outside the app
            frame; each step draws its own top bar (OnboardingFrame). */}
        <Route
          element={
            <ProtectedRoute requiredRoles="student" allowIncompleteOnboarding>
              <main>
                <Outlet />
              </main>
            </ProtectedRoute>
          }
        >
          <Route path="/onboarding/skills" element={<SkillsPage />} />
          <Route path="/onboarding/aspiration" element={<AspirationPage />} />
          <Route path="/onboarding/results" element={<ResultsPage />} />
        </Route>

        {/* Protected student/admin routes inside shell */}
        <Route
          element={
            <ProtectedRoute requiredRoles={["student", "mfox"]}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<StudentHomePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/leaderboard" element={gated("xp", <LeaderboardPage />)} />
          <Route path="/tests" element={<TestListPage />} />
          <Route path="/roadmap/*" element={<RoadmapPage />} />
          <Route path="/opportunities/*" element={gated("opportunities", <OpportunitiesStudentRoutes />)} />
          <Route path="/mentor-assessment/*" element={<MentorAssessmentRoutes />} />
          {/* Only where code runs safely; see playgroundEnabled. */}
          {playgroundEnabled ? (
            <>
              <Route path="/playground" element={<TopicListPage />} />
              <Route
                path="/playground/:topicId"
                element={<ProblemListRoute />}
              />
              <Route
                path="/playground/:topicId/:problemId"
                element={<PlaygroundRoute />}
              />
            </>
          ) : null}
        </Route>

        {/* Protected course routes (outside shell) */}
        <Route
          path="/course"
          element={
            <ProtectedRoute requiredRoles={["student", "mfox"]}>
              <CourseHomeRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/course/:courseId/:lessonId"
          element={
            <ProtectedRoute requiredRoles={["student", "mfox"]}>
              <CoursePageRoute />
            </ProtectedRoute>
          }
        />

        {/* The live mentor assessment session - outside the shell for the same
            immersive, no-sidebar reason as /tests/:testId. The rest of the flow
            (including the old /waiting URL) is in-shell under /mentor-assessment/*. */}
        <Route
          path="/mentor-assessment/session"
          element={
            <ProtectedRoute requiredRoles={["student", "mfox"]}>
              <MentorAssessmentWorkspacePage />
            </ProtectedRoute>
          }
        />
        {/* The real mentor portal: its own sidebar shell, mentors only. */}
        <Route
          path="/mentor/*"
          element={
            <ProtectedRoute requiredRoles="mentor">
              <MentorRoutes />
            </ProtectedRoute>
          }
        />

        {/* Stand-in evaluation page for demos, where one browser plays both
            student and mentor. Any signed-in student could open it and score
            themselves, so it exists only in dev and demo builds; real mentors
            use /mentor. */}
        {demoShortcuts ? (
          <Route
            path="/mentor-assessment/mentor/:bookingId/evaluate"
            element={
              <ProtectedRoute requiredRoles={["student", "mfox"]}>
                <MentorEvaluationPage />
              </ProtectedRoute>
            }
          />
        ) : null}

        {/* Test route outside shell - needs its own protection */}
        <Route
          path="/tests/:testId"
          element={
            <ProtectedRoute requiredRoles={["student", "mfox"]}>
              <TestPageRoute />
            </ProtectedRoute>
          }
        />

        {/* Admin (MFox only) and college admin (scoped to one college); the pages and their sidebar shells live in the admin remote. */}
        <Route
          path="/admin/college/*"
          element={
            <ProtectedRoute requiredRoles="college-admin">
              <CollegeAdminRoutes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute requiredRoles="mfox">
              <AdminRoutes />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
